// eslint-disable-next-line lodash/import-scope
import get from 'lodash/get';
import P from 'prop-types';
import { group, stringSelectSpec } from '../../lib/editing-configs';
import SpotifyPlayer from '../../lib/spotify-player';
import SoundCloudPlayer from '../../lib/soundcloud-player';
import YouTubePlayer from '../../lib/youtube-player/youtube-player';
import Mp4VideoPlayer from '../../lib/mp4-video-player';

export const mediaTypes = {
  YOUTUBE: 'youtube',
  MP4_VIDEO: 'mp4',
  SOUNDCLOUD: 'soundcloud',
  SPOTIFY: 'spotify',
};

const UniversalMedia = ({
  mediaType,
  // Audio (Spotify | SoundCloud)
  podcastMediaHight,
  podcastMediaWidth,
  spotifyEpisodeUrl,
  soundCloudEpisodeId,
  soundCloudPlayerColor,
  // YouTube
  youtubeVideoSlug,
  youtubeMaxWidth,
  youtubeMaxHeight,
  youtubePlaceholderImageUrl,
  youtubePlayButtonImageUrl,
  defaultPlayButtonIcon,
  imagePlaceholderCoverSize,
  enableModestbranding,
  privacyModeToggle,
  controlsToggle,
  // MP4 Video
  videoUrl,
  videoTrack,
  videoTrackKind,
  videoAutoplay,
  videoControls,
  videoMuted,
  videoLoop,
  videoMaxHeight,
  videoMaxWidth,
}) => {
  switch (mediaType) {
    case mediaTypes.SPOTIFY:
      return (
        <SpotifyPlayer
          spotifyEpisodeUrl={spotifyEpisodeUrl}
          podcastMediaHight={podcastMediaHight}
          podcastMediaWidth={podcastMediaWidth}
        />
      );
    case mediaTypes.SOUNDCLOUD:
      return (
        <SoundCloudPlayer
          soundCloudEpisodeId={soundCloudEpisodeId}
          podcastMediaHight={podcastMediaHight}
          podcastMediaWidth={podcastMediaWidth}
          soundCloudPlayerColor={soundCloudPlayerColor}
        />
      );
    case mediaTypes.YOUTUBE:
      return (
        <YouTubePlayer
          controlsToggle={controlsToggle}
          defaultPlayButtonIcon={defaultPlayButtonIcon}
          enableModestbranding={enableModestbranding}
          imagePlaceholderCoverSize={imagePlaceholderCoverSize}
          privacyModeToggle={privacyModeToggle}
          youtubeMaxHeight={youtubeMaxHeight}
          youtubeMaxWidth={youtubeMaxWidth}
          youtubePlaceholderImageUrl={youtubePlaceholderImageUrl}
          youtubePlayButtonImageUrl={youtubePlayButtonImageUrl}
          youtubeVideoSlug={youtubeVideoSlug}
        />
      );
    case mediaTypes.MP4_VIDEO:
      return (
        <Mp4VideoPlayer
          videoUrl={videoUrl}
          videoTrack={videoTrack}
          videoTrackKind={videoTrackKind}
          videoAutoplay={videoAutoplay}
          videoControls={videoControls}
          videoMuted={videoMuted}
          videoLoop={videoLoop}
          videoMaxHeight={videoMaxHeight}
          videoMaxWidth={videoMaxWidth}
        />
      );
    default:
      return null;
  }
};

UniversalMedia.displayName = 'UniversalMedia';

UniversalMedia.propTypes = {
  mediaType: P.oneOf([...Object.values(mediaTypes), '']),
  ...SpotifyPlayer.propTypes,
  ...SoundCloudPlayer.propTypes,
  ...YouTubePlayer.propTypes,
  ...Mp4VideoPlayer.propTypes,
};

UniversalMedia.defaultProps = {
  mediaType: '',
};

const isTestEnv = process?.env?.NODE_ENV;
/**
 * @param {*} componentDocument
 * @param {string} fieldLabel Field Label in the Editor
 * @param {Array.<'mp4'|'youtube'|'soundcloud'|'spotify'>} allowedControlsList List of controls to be shown in the editor
 * @param {string} fieldName
 * @returns
 */
UniversalMedia.getEditingConfig = (
  componentDocument,
  fieldLabel = 'Universal Media',
  allowedControlsList = isTestEnv
    ? ['mp4', 'youtube', 'soundcloud', 'spotify']
    : [],
  fieldName = 'universalMedia'
) => {
  const allowedMediaTypesOptions = [
    { label: 'Select', value: '' },
    ...((allowedControlsList.includes('mp4') && [
      { label: 'MP4 Video', value: mediaTypes.MP4_VIDEO },
    ]) ||
      []),
    ...((allowedControlsList.includes('youtube') && [
      { label: 'YouTube', value: mediaTypes.YOUTUBE },
    ]) ||
      []),
    ...((allowedControlsList.includes('soundcloud') && [
      { label: 'SoundCloud', value: mediaTypes.SOUNDCLOUD },
    ]) ||
      []),
    ...((allowedControlsList.includes('spotify') && [
      { label: 'Spotify', value: mediaTypes.SPOTIFY },
    ]) ||
      []),
  ];

  const spotifyInputConfig =
    get(componentDocument, `props.${fieldName}.mediaType`, '') ===
    mediaTypes.SPOTIFY
      ? [...SpotifyPlayer.getEditingConfigs(componentDocument, fieldName)]
      : [];

  const soundCloudInputConfig =
    get(componentDocument, `props.${fieldName}.mediaType`, '') ===
    mediaTypes.SOUNDCLOUD
      ? [...SoundCloudPlayer.getEditingConfigs(componentDocument, fieldName)]
      : [];

  const youtubeInputConfig =
    get(componentDocument, `props.${fieldName}.mediaType`, '') ===
    mediaTypes.YOUTUBE
      ? [...YouTubePlayer.getEditingConfigs(componentDocument, fieldName)]
      : [];

  const mp4VideoInputConfig =
    get(componentDocument, `props.${fieldName}.mediaType`, '') ===
    mediaTypes.MP4_VIDEO
      ? [...Mp4VideoPlayer.getEditingConfigs(componentDocument, fieldName)]
      : [];

  return [
    group(fieldLabel, [
      stringSelectSpec(
        `${fieldName}.mediaType`,
        'Media Type',
        allowedMediaTypesOptions
      ),
      ...spotifyInputConfig,
      ...soundCloudInputConfig,
      ...youtubeInputConfig,
      ...mp4VideoInputConfig,
    ]),
  ];
};

export default UniversalMedia;
